import { useEffect } from 'react';
import SEO from '../../../components/SEO';

export default function CommercialSecurityToronto() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    {
      question: "Does Toronto require commercial alarm permits, and what happens without one?",
      answer: "Yes. The City of Toronto requires commercial alarm system permits under Toronto Municipal Code Chapter 237. Annual fees are $57–$112 depending on occupancy type. Without a valid permit, Toronto Police Service may delay response to alarm calls and issue false alarm fines starting at $150 per unverified dispatch. After three unverified dispatches in a 12-month period, TPS may suspend police response entirely. Alliance Security manages all permit applications, renewal filings, and false alarm dispute documentation for our Toronto commercial clients."
    },
    {
      question: "What makes security for King-Spadina construction corridor businesses different?",
      answer: "Active construction creates three specific security problems that standard residential-grade systems can't handle: (1) continuous vibration disables standard PIR motion sensors, triggering false alarms that burn through your TPS dispatch priority — commercial systems require vibration-compensated detection; (2) hoarding and scaffolding create camera blind zones that need repositioning every 6–12 weeks as construction stages advance; (3) contractor access to adjacent properties elevates piggyback entry risk at your own building. Our King-Spadina installations use hardwired vibration-tolerant detection, quarterly camera repositioning protocols, and anti-passback access control to close all three gaps."
    },
    {
      question: "How much does commercial security cost in Toronto?",
      answer: "Toronto commercial security typically ranges from $2,800–$9,000 for installation plus $55–$160/month for ULC-certified monitoring. Ground-floor Entertainment District retail averages $3,500–$5,500 for a smash-and-grab hardening package. Liberty Village tech offices with server room access control typically invest $5,000–$8,500. King-Spadina construction-adjacent properties average $4,000–$7,000 depending on building footprint and current construction stage. Most Toronto clients recover installation costs within 12–16 months through insurance premium reductions of 18–25%."
    }
  ];

  const neighborhoods = [
    "King-Spadina Corridor",
    "Liberty Village",
    "Entertainment District",
    "Financial District",
    "Distillery District",
    "Yorkville",
    "Queen West",
    "Kensington Market",
    "Yonge & Eglinton",
    "North York Centre",
    "Etobicoke Business District",
    "Scarborough Town Centre"
  ];

  const otherCities = [
    { name: "Mississauga", slug: "mississauga" },
    { name: "Brampton", slug: "brampton" },
    { name: "Oakville", slug: "oakville" },
    { name: "Hamilton", slug: "hamilton" },
    { name: "Richmond Hill", slug: "richmond-hill" },
    { name: "Vaughan", slug: "vaughan" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://alliancesecuritysystems.com/commercial-security/toronto#business",
        "name": "Alliance Security Systems - Commercial Security Toronto",
        "image": "https://public.readdy.ai/ai/img_res/f6b70374fe77ae12f84ca783a7e9126d.jpg",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Toronto",
          "addressRegion": "ON",
          "addressCountry": "CA"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "43.6532",
          "longitude": "-79.3832"
        },
        "url": "https://alliancesecuritysystems.com/commercial-security/toronto",
        "telephone": "+1-888-458-9181",
        "priceRange": "$$-$$$",
        "areaServed": {
          "@type": "City",
          "name": "Toronto"
        },
        "serviceType": [
          "Commercial Security Systems",
          "Business Alarm Systems",
          "Commercial CCTV",
          "Access Control Systems",
          "Construction Site Security",
          "Tech Office Security",
          "Retail After-Hours Security"
        ]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://alliancesecuritysystems.com/commercial-security/toronto#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://alliancesecuritysystems.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Commercial Security",
            "item": "https://alliancesecuritysystems.com/commercial-security"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Toronto",
            "item": "https://alliancesecuritysystems.com/commercial-security/toronto"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "@id": "https://alliancesecuritysystems.com/commercial-security/toronto#faq",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  return (
    <>
      <SEO
        title="Commercial Security Company Toronto | Construction, Tech & Retail Systems"
        description="Best commercial security company in Toronto. ULC-certified systems for the King-Spadina construction corridor, Liberty Village tech cluster & Entertainment District retail. 200+ Toronto businesses protected. Free assessment."
        keywords="commercial security Toronto, business security Toronto, King Spadina construction security, Liberty Village office security, Entertainment District retail security, Toronto business alarm"
        canonical="/commercial-security/toronto"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-white">

        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Toronto%20downtown%20skyline%20CN%20Tower%20King%20Street%20West%20commercial%20district%20modern%20glass%20towers%20twilight%20blue%20hour%20professional%20cityscape%20Ontario%20financial%20district%20aerial%20view&width=1920&height=800&seq=comm-toronto-hero-v2&orientation=landscape"
            alt="Commercial Security Toronto - King-Spadina, Liberty Village and Entertainment District"
            title="Toronto Commercial Security Systems"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center text-white">
            <nav className="flex items-center justify-center gap-2 text-sm mb-6" itemScope itemType="https://schema.org/BreadcrumbList">
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Home</span>
                </a>
                <meta itemProp="position" content="1" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="/commercial-security" itemProp="item" className="hover:text-teal-400 transition-colors">
                  <span itemProp="name">Commercial Security</span>
                </a>
                <meta itemProp="position" content="2" />
              </span>
              <span className="text-white/60">/</span>
              <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name" className="text-teal-400">Toronto</span>
                <meta itemProp="position" content="3" />
              </span>
            </nav>

            <h1 className="text-5xl font-bold mb-6">Commercial Security Toronto</h1>
            <p className="text-xl mb-8 text-white/90">
              Three Zones, Three Security Profiles — Construction Corridor, Tech Cluster &amp; After-Hours Retail
            </p>
            <button
              onClick={scrollToForm}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              Get Free Security Assessment
            </button>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">200+</div>
                <div className="text-sm text-gray-300">Toronto Commercial Clients</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">&lt;3 min</div>
                <div className="text-sm text-gray-300">Verified Alarm Dispatch</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">25+ yrs</div>
                <div className="text-sm text-gray-300">Serving Toronto Businesses</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-400 mb-2">21%</div>
                <div className="text-sm text-gray-300">Avg Insurance Discount</div>
              </div>
            </div>
          </div>
        </section>

        {/* Three-Zone Commercial Challenge Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Toronto&apos;s Three Commercial Security Zones</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The King-Spadina construction corridor, Liberty Village tech cluster, and Entertainment District retail strip each carry a distinct crime profile — and none of them are solved by the same system
            </p>

            <div className="grid md:grid-cols-3 gap-8">

              {/* King-Spadina Construction Corridor */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-4-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">King-Spadina Construction Corridor</h3>
                <p className="text-gray-600 mb-4">
                  The Bathurst-to-University stretch of King and Spadina is one of North America&apos;s densest active urban construction zones. For adjacent commercial tenants, that creates three specific security failures that standard residential-grade systems cannot handle: continuous construction vibration triggers false alarm surges that burn through TPS dispatch priority; hoarding and scaffolding rotate every 8–12 weeks, creating new camera blind zones each phase; and contractor access to neighboring properties elevates piggyback entry risk at your own building. Every standard wireless PIR sensor on a King-Spadina construction block will false-alarm itself off TPS&apos;s priority list within 90 days without vibration compensation.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>TPS 14 Division data:</strong> Ground-floor commercial break-ins in active construction corridors run 3.4x higher than comparable blocks without adjacent construction
                </div>
              </div>

              {/* Liberty Village Tech Cluster */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-computer-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Liberty Village Tech Cluster</h3>
                <p className="text-gray-600 mb-4">
                  Liberty Village&apos;s repurposed industrial loft architecture creates an access control environment that most office security designs ignore entirely: freight elevators with no credential enforcement, multiple ground-level building entries left on shared fob systems, and parking structures that connect to three or four separate tenants with no zoning. The highest-value assets — server rooms, development workstations, and IP-loaded laptops — are protected by nothing more than the same keycard that opens the front lobby. Tailgating is the dominant attack vector. Buildings without anti-passback enforcement and per-floor credential zoning are the highest-risk cohort in the Village.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>TPS 14 Division:</strong> Liberty Village after-hours office break-ins increased 44% in 2024 — hardware theft averaging $38,000 per incident targeting development workstations and server components
                </div>
              </div>

              {/* Entertainment District After-Hours Retail */}
              <div className="bg-gray-50 p-8 rounded-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-store-2-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Entertainment District After-Hours Retail</h3>
                <p className="text-gray-600 mb-4">
                  Ground-floor retailers and restaurants along John Street, Peter Street, and Blue Jays Way face a vulnerability window that no other Toronto commercial zone shares: 2:00–5:00 AM, when neighboring clubs and bars empty, foot traffic collapses, and staff security presence is gone. Smash-and-grab operations in this window average 4–8 minutes from entry to exit — well within the response window of an unverified alarm. The key countermeasure is not a louder siren — it&apos;s verified alarm dispatch that cuts TPS response to under 3 minutes, combined with storefront glass-break sensors wired directly to immediate audio notification at our monitoring centre for instant two-way deterrence before police arrival.
                </p>
                <div className="text-sm text-gray-500">
                  <strong>TPS Division 14 &amp; 52 data:</strong> Entertainment District smash-and-grab incidents cluster in the 2–4 AM window at 71% — the highest after-midnight retail crime concentration in Toronto
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TPS Callout Panel */}
        <section className="py-16 bg-teal-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="flex-shrink-0 w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-shield-star-line text-3xl text-white"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-3">Why Toronto Police Service Division Coverage Matters to Your Security Design</h2>
                <p className="text-gray-700 leading-relaxed">
                  Unlike Peel Regional Police&apos;s unified regional dispatch or Hamilton Police Service&apos;s single-command structure, <strong>Toronto Police Service operates through 17 divisional commands</strong> — each with its own false alarm strike tracking, alarm response prioritization protocols, and commercial liaison unit. A business on King Street West may be in Division 14. Two blocks east, it&apos;s Division 52. The division boundary changes your false alarm permit tracking, your response priority tier, and the commercial crime unit contact for investigation follow-up. Security providers who design systems without knowing which TPS division serves your specific address are building the wrong system for the wrong response environment. Our Toronto commercial installations are calibrated to divisional command boundaries, false alarm bylaw strike thresholds, and the specific dispatch protocols of whichever TPS division governs your property.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Commercial Security Solutions for Toronto</h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Purpose-built for construction corridor vibration tolerance, tech office access control, and after-hours retail glass-break response
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <a href="/camera-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-camera-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Commercial Cameras</h3>
                <p className="text-gray-600 mb-4">4K resolution, facial recognition, LPR for parkade coverage, AI perimeter analytics, construction-zone repositioning protocols</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/alarm-packages" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-alarm-warning-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Business Alarms</h3>
                <p className="text-gray-600 mb-4">ULC-certified monitoring, vibration-tolerant detection, glass-break sensors, panic buttons, direct TPS divisional dispatch integration</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/access-control" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-key-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Access Control</h3>
                <p className="text-gray-600 mb-4">Anti-passback enforcement, per-floor credential zoning, freight elevator control, biometric server room access, visitor management</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>

              <a href="/live-monitoring" className="bg-white p-8 rounded-lg hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className="ri-eye-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Live Monitoring</h3>
                <p className="text-gray-600 mb-4">24/7 video verification, two-way audio deterrence for after-hours retail, real-time threat assessment, sub-3-minute TPS verified dispatch</p>
                <div className="text-teal-600 font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Case Study */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Toronto Commercial Security: Case Study</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              A documented outcome from the Liberty Village tech cluster
            </p>

            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-10">
                  <div className="text-sm font-semibold text-teal-600 mb-2 uppercase tracking-wide">Liberty Village — Tech Studio Office</div>
                  <h3 className="text-2xl font-bold mb-4">Two Hardware Thefts. $91,000 in Losses. One Access Control Gap.</h3>
                  <p className="text-gray-600 mb-6">
                    A 22-person software development studio on Atlantic Avenue experienced two after-hours break-ins in four months, totalling $91,000 in development hardware and $45,000 in business interruption losses. Both incidents used the same entry method: a shared fob that a former contractor had duplicated, accessing the freight elevator bank with no audit trail and no anti-passback barrier on the studio floor.
                  </p>
                  <p className="text-gray-600 mb-6">
                    Alliance Security replaced the single-credential lobby system with per-floor keycard zones, installed anti-passback enforcement on the freight elevator, added biometric access to the server room, and connected the office to our ULC-certified monitoring centre with 4K camera coverage of all interior entry points.
                  </p>
                  <p className="text-gray-600">
                    Outcome: zero incidents in 11 months following installation. The studio&apos;s commercial property insurer added a technology equipment endorsement at a 19% discount rate based on the access control documentation. TPS 14 Division recovered one of the original theft suspects using our archived camera footage from the second incident.
                  </p>
                </div>
                <div className="relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Liberty%20Village%20Toronto%20tech%20office%20loft%20industrial%20building%20interior%20modern%20workspace%20server%20room%20access%20control%20biometric%20security%20professional%20photography%20warm%20evening%20lighting%20Ontario&width=600&height=500&seq=toronto-liberty-case-study&orientation=portrait"
                    alt="Liberty Village Tech Office Security Case Study - Toronto"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 divide-x divide-teal-500/30 bg-teal-600 text-white">
                <div className="p-6 text-center">
                  <div className="text-3xl font-bold mb-1">$136K</div>
                  <div className="text-sm text-teal-100">Total losses — 2 incidents, 4 months</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-3xl font-bold mb-1">1 fob</div>
                  <div className="text-sm text-teal-100">The single access control gap that enabled both thefts</div>
                </div>
                <div className="p-6 text-center">
                  <div className="text-3xl font-bold mb-1">Zero</div>
                  <div className="text-sm text-teal-100">Incidents in 11 months post-install</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods Served */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Toronto Business Districts We Serve</h2>
            <p className="text-center text-gray-600 mb-12">
              Protecting commercial properties across every Toronto neighbourhood — from the Entertainment District to Scarborough
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {neighborhoods.map((neighborhood, index) => (
                <div key={index} className="bg-white px-6 py-4 rounded-lg text-center hover:bg-teal-50 transition-colors">
                  <div className="font-semibold text-gray-800">{neighborhood}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-4">Toronto Commercial Security Resources</h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
              In-depth guides to Toronto&apos;s commercial security zones and service coverage
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <a href="/service-areas/toronto" className="bg-gray-50 rounded-lg overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=Toronto%20city%20skyline%20residential%20commercial%20neighbourhood%20CN%20Tower%20backdrop%20Ontario%20urban%20landscape%20warm%20golden%20hour%20daylight%20professional%20photography%20aerial%20view&width=700&height=320&seq=toronto-service-area-card-v2&orientation=landscape"
                  alt="Toronto Security Service Area"
                  className="w-full h-40 object-cover object-top"
                />
                <div className="p-6">
                  <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-2">Service Area</div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-teal-600 transition-colors">Toronto Security Service Area</h3>
                  <p className="text-gray-600 text-sm">All residential and commercial security services available across Toronto, from downtown to Etobicoke and Scarborough.</p>
                </div>
              </a>
              <a href="/commercial-security" className="bg-gray-50 rounded-lg overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=GTA%20Greater%20Toronto%20Area%20commercial%20security%20systems%20modern%20office%20building%20camera%20access%20control%20professional%20installation%20business%20district%20Ontario%20corporate%20campus&width=700&height=320&seq=toronto-commercial-hub-card&orientation=landscape"
                  alt="GTA Commercial Security Solutions"
                  className="w-full h-40 object-cover object-top"
                />
                <div className="p-6">
                  <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-2">Commercial Security Hub</div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-teal-600 transition-colors">GTA Commercial Security Solutions</h3>
                  <p className="text-gray-600 text-sm">Full overview of commercial security systems, pricing, industries served, and all GTA city-specific guides.</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Toronto Commercial Security FAQs</h2>
            <p className="text-center text-gray-600 mb-12">
              Common questions from Toronto business owners
            </p>

            <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white p-8 rounded-lg" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                  <h3 className="text-xl font-bold mb-3 flex items-start gap-3" itemProp="name">
                    <span className="text-teal-500 flex-shrink-0">Q:</span>
                    <span>{faq.question}</span>
                  </h3>
                  <div className="pl-8 text-gray-600" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p itemProp="text">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact-form" className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-4">Get Your Free Toronto Security Assessment</h2>
            <p className="text-center text-gray-600 mb-12">
              On-site evaluation, custom proposal, and transparent pricing — no obligation
            </p>

            <form
              className="space-y-6 bg-gray-50 p-8 rounded-lg"
              data-readdy-form
              id="toronto-commercial-form"
              action="https://readdy.ai/api/form/d6ggpceqpmcju3gsd8o0"
              method="POST"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Business Name</label>
                  <input
                    type="text"
                    name="business_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Your Company Ltd."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Your Name</label>
                  <input
                    type="text"
                    name="contact_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="416-XXX-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Business Address in Toronto</label>
                <input
                  type="text"
                  name="business_address"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 King St W, Toronto, ON"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Business Zone</label>
                <select
                  name="business_zone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select Zone (optional)</option>
                  <option value="king_spadina">King-Spadina Construction Corridor</option>
                  <option value="liberty_village">Liberty Village</option>
                  <option value="entertainment_district">Entertainment District</option>
                  <option value="financial_district">Financial District</option>
                  <option value="yorkville">Yorkville</option>
                  <option value="queen_west">Queen West / Kensington</option>
                  <option value="north_york">North York</option>
                  <option value="etobicoke">Etobicoke</option>
                  <option value="scarborough">Scarborough</option>
                  <option value="other">Other Toronto Area</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Industry</label>
                <select
                  name="industry"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select Industry</option>
                  <option value="tech_office">Tech / Software / Creative Office</option>
                  <option value="retail">Retail</option>
                  <option value="restaurant_hospitality">Restaurant / Bar / Hospitality</option>
                  <option value="office">Corporate Office</option>
                  <option value="construction_adjacent">Construction-Adjacent Commercial</option>
                  <option value="warehouse">Warehouse / Logistics</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Security Needs (optional)</label>
                <textarea
                  name="security_details"
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Describe your security concerns, construction proximity, current system, or specific requirements..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">Maximum 500 characters</div>
              </div>

              <button
                type="submit"
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Free Assessment
              </button>
            </form>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12">Commercial Security in Other GTA Cities</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {otherCities.map((city, index) => (
                <a
                  key={index}
                  href={`/commercial-security/${city.slug}`}
                  className="bg-white px-6 py-4 rounded-lg text-center hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  <div className="font-semibold text-gray-800 group-hover:text-teal-600 transition-colors">{city.name}</div>
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="/commercial-security/service-areas" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center gap-2">
                View All Service Areas <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Protect Your Toronto Business?</h2>
            <p className="text-xl mb-8 text-white/90">
              Join 200+ Toronto companies trusting us with their security — construction corridor to Entertainment District
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={scrollToForm}
                className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Assessment
              </button>
              <a
                href="/commercial-security"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-block"
              >
                View All Solutions
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
